import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Details, Heading, List, Paragraph, Skeleton, Switch, Tag } from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import type { TemplateVersionDetail, TemplateVersionSummary } from '../../../../api/hms';
import { useHmsTemplateVersionQuery } from '../../../../hooks/queries/hms/useHmsQueries';
import { approvedBy, formatDay } from '../../hmsFormat';
import { formatTemplateChange, versionStatus } from '../riskFactorsView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskFactors.module.css';

type Props = {
  id: number;
  versions: TemplateVersionSummary[];
  backLabel: string;
  onBack: () => void;
  onAvailabilityChange: (id: number, availableForNew: boolean) => void;
  savingAvailability: boolean;
  availabilityFailed: boolean;
};

export const VersionView = ({
  id,
  versions,
  backLabel,
  onBack,
  onAvailabilityChange,
  savingAvailability,
  availabilityFailed,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useHmsTemplateVersionQuery(id);
  const summary = versions.find((version) => version.id === id);
  const availableCount = versions.filter((version) => version.status === 'APPROVED' && version.availableForNew).length;

  const back = (
    <Button variant="tertiary" data-size="sm" onClick={onBack} className={classes.backButton}>
      <ArrowLeftIcon aria-hidden />
      {backLabel}
    </Button>
  );

  if (isLoading || !summary) {
    return (
      <section className={layout.card} aria-busy="true">
        {back}
        <Skeleton variant="rectangle" width="100%" height={320} />
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className={layout.card}>
        {back}
        <Alert data-color="danger">
          <Paragraph data-size="sm">{t('hrRiskFactors.error.message')}</Paragraph>
        </Alert>
      </section>
    );
  }

  const status = versionStatus(summary);
  const onlyAvailable = summary.availableForNew && availableCount === 1;
  const date = formatDay(data.approval?.approvedAt ?? data.createdAt, t, i18n.language);
  const by = data.approval ? approvedBy(data.approval, t) : t('hms.trustpulse');

  return (
    <section aria-labelledby="hr-rf-version" className={layout.card} data-gap="md">
      {back}
      <div className={classes.versionHeading}>
        <div className={layout.cardHeading}>
          <Heading level={2} data-size="md" id="hr-rf-version">
            {t('hrRiskFactors.version.title', { version: data.version })}
          </Heading>
          <Paragraph data-size="sm" className={layout.subtle}>
            {t('hrRiskFactors.version.meta', {
              date,
              by,
              factors: data.factorCount,
              questions: data.questionCount,
            })}
          </Paragraph>
        </div>
        <Tag data-color={status.color}>{t(status.label)}</Tag>
      </div>
      <div className={classes.availability}>
        <Switch
          data-size="sm"
          position="end"
          className={classes.availabilitySwitch}
          label={t('hrRiskFactors.version.available')}
          description={t(
            onlyAvailable
              ? 'hrRiskFactors.version.onlyAvailable'
              : summary.availableForNew
                ? 'hrRiskFactors.version.availableHelp'
                : 'hrRiskFactors.version.unavailableHelp',
          )}
          checked={summary.availableForNew}
          readOnly={onlyAvailable || savingAvailability}
          onClick={(event) => {
            if (onlyAvailable || savingAvailability) event.preventDefault();
          }}
          onChange={(event) => onAvailabilityChange(data.id, event.target.checked)}
        />
        {availabilityFailed && (
          <Alert data-color="danger">
            <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
          </Alert>
        )}
      </div>
      <VersionDetails version={data} />
    </section>
  );
};

/** What changed from the version before, and the factors with their questions */
export const VersionDetails = ({ version: data }: { version: TemplateVersionDetail }): ReactElement => {
  const { t } = useTranslation();
  const changes = data.changes.map((change) => formatTemplateChange(change, t, false, data.languages));
  return (
    <>
      <div className={classes.versionSection}>
        <Heading level={3} data-size="2xs">
          {data.previousVersion
            ? t('hrRiskFactors.version.changedFrom', { version: data.previousVersion })
            : t('hrRiskFactors.version.first')}
        </Heading>
        <List.Unordered data-size="sm">
          {data.previousVersion ? (
            changes.length > 0 ? (
              changes.map((change) => <List.Item key={change}>{change}</List.Item>)
            ) : (
              <List.Item>{t('hrRiskFactors.version.noChanges')}</List.Item>
            )
          ) : (
            <List.Item>{t('hrRiskFactors.version.standard', { count: data.factorCount })}</List.Item>
          )}
        </List.Unordered>
      </div>
      <div className={classes.versionSection}>
        <Heading level={3} data-size="2xs">
          {t('hrRiskFactors.version.content')}
        </Heading>
        {data.factors.map((factor) => (
          <Details key={factor.key}>
            <Details.Summary>
              {t('hrRiskFactors.version.factorSummary', { name: factor.name, count: factor.questions.length })}
            </Details.Summary>
            <Details.Content>
              <div className={classes.versionFactor}>
                {factor.description && (
                  <Paragraph data-size="sm" className={layout.subtle}>
                    {factor.description}
                  </Paragraph>
                )}
                <List.Ordered data-size="sm">
                  {factor.questions.map((question) => (
                    <List.Item key={question.key}>
                      {question.text}{' '}
                      <span className={layout.subtle}>
                        {t(
                          question.highIsGood
                            ? 'hrRiskFactors.version.highIsGood'
                            : 'hrRiskFactors.version.highIsProblem',
                        )}
                      </span>
                    </List.Item>
                  ))}
                </List.Ordered>
              </div>
            </Details.Content>
          </Details>
        ))}
      </div>
    </>
  );
};
